import React, { useEffect, useState, useMemo } from 'react';
import { listNotifications, markNotificationAsRead, markAllNotificationsAsRead } from '../../services/notifications';

const statusBadge = (category) => {
  const map = {
    license: { bg: 'bg-emerald-50', text: 'text-emerald-700' },
    message: { bg: 'bg-blue-50', text: 'text-blue-700' },
    payment: { bg: 'bg-amber-50', text: 'text-amber-700' },
    translation: { bg: 'bg-purple-50', text: 'text-purple-700' },
    account: { bg: 'bg-gray-50', text: 'text-gray-700' },
    system: { bg: 'bg-slate-50', text: 'text-slate-700' },
    default: { bg: 'bg-slate-50', text: 'text-slate-700' },
  };
  return map[category] || map.default;
};

const NotificationList = () => {
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState({ read: 'all', category: 'all' });
  const [unreadCount, setUnreadCount] = useState(0);

  const loadData = async (page = 1, filters = filter) => {
    try {
      setLoading(true);
      setError(null);
      const params = { page, limit: pagination.limit || 20 };
      if (filters.read === 'unread') params.read = false;
      if (filters.category !== 'all') params.category = filters.category;
      const res = await listNotifications(params);
      if (!res.success) throw new Error(res.error || 'Failed to load notifications');
      const data = res.data || {};
      setItems(data.notifications || []);
      setPagination(data.pagination || { page: 1, limit: 20, total: 0, pages: 0 });
      setUnreadCount(data.unreadCount || 0);
    } catch (e) {
      setError(e.message || 'Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData(1, filter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter.read, filter.category]);

  const handleMarkRead = async (id) => {
    try {
      await markNotificationAsRead(id);
      await loadData(pagination.page);
    } catch (e) {
      console.error(e);
      setError(e.message || 'Failed to mark as read');
    }
  };

  const handleMarkAll = async () => {
    try {
      await markAllNotificationsAsRead();
      await loadData(1);
    } catch (e) {
      console.error(e);
      setError(e.message || 'Failed to mark all as read');
    }
  };

  const categories = useMemo(
    () => ['all', 'license', 'message', 'payment', 'translation', 'account', 'system'],
    []
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">Unread: {unreadCount}</p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={filter.category}
            onChange={(e) => setFilter((f) => ({ ...f, category: e.target.value }))}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-400"
          >
            {categories.map((c) => (
              <option key={c} value={c}>
                {c === 'all' ? 'All categories' : c.charAt(0).toUpperCase() + c.slice(1)}
              </option>
            ))}
          </select>
          <select
            value={filter.read}
            onChange={(e) => setFilter((f) => ({ ...f, read: e.target.value }))}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-400"
          >
            <option value="all">All</option>
            <option value="unread">Unread only</option>
          </select>
          <button
            onClick={handleMarkAll}
            className="px-4 py-2 rounded-lg text-sm font-medium border border-gray-300 text-gray-700 hover:bg-gray-50"
          >
            Mark all read
          </button>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        {loading && <div className="p-4 text-sm text-gray-500">Loading...</div>}
        {error && <div className="p-4 text-sm text-red-600">{error}</div>}
        {!loading && items.length === 0 && (
          <div className="p-4 text-sm text-gray-500">No notifications.</div>
        )}
        <ul className="divide-y divide-gray-200">
          {items.map((n) => {
            const badge = statusBadge(n.category);
            return (
              <li key={n._id || n.id} className={`p-4 ${!n.read ? 'bg-emerald-50/40' : ''}`}>
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
                        {n.category || 'system'}
                      </span>
                      {!n.read && <span className="text-[10px] uppercase tracking-wide text-emerald-600 font-semibold">Unread</span>}
                    </div>
                    <h3 className="mt-2 text-sm font-semibold text-gray-900">{n.title}</h3>
                    <p className="mt-1 text-sm text-gray-600 whitespace-pre-line">{n.body}</p>
                    {n.meta?.actionUrl && (
                      <a
                        href={n.meta.actionUrl}
                        className="mt-2 inline-block text-sm text-emerald-600 hover:text-emerald-700 underline"
                      >
                        {n.meta.actionText || 'Open'}
                      </a>
                    )}
                    <p className="mt-2 text-xs text-gray-500">
                      {n.createdAt ? new Date(n.createdAt).toLocaleString() : ''}
                    </p>
                  </div>
                  {!n.read && (
                    <button
                      onClick={() => handleMarkRead(n._id || n.id)}
                      className="ml-4 px-3 py-2 rounded-lg text-xs font-medium bg-emerald-500 text-white hover:bg-emerald-600"
                    >
                      Mark read
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
        {pagination.pages > 1 && (
          <div className="flex items-center justify-between p-4 border-t border-gray-200">
            <div className="text-sm text-gray-500">
              Page {pagination.page} of {pagination.pages}
            </div>
            <div className="flex gap-2">
              <button
                disabled={pagination.page <= 1}
                onClick={() => loadData(pagination.page - 1)}
                className="px-3 py-2 rounded-lg text-sm border border-gray-300 text-gray-700 disabled:opacity-50"
              >
                Prev
              </button>
              <button
                disabled={pagination.page >= pagination.pages}
                onClick={() => loadData(pagination.page + 1)}
                className="px-3 py-2 rounded-lg text-sm border border-gray-300 text-gray-700 disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationList;

